import { axiosInstance } from '@/Api/axiosV2Config';
import type { ExternalUser, CreateExternalUserDto, UpdateExternalUserDto } from '@/features/external-users/types/external-user.types';
import type { AuditLog } from '@/features/external-users/types/audit-log.types';

/**
 * API client partage pour la gestion des utilisateurs externes
 * Contient les methodes de lecture + ecriture utilisees par admin et outil-pro
 */
export const externalUsersApi = {
  // ==================== QUERIES (lecture) ====================

  getAll: async (filters: Record<string, any> = {}) => {
    const params: Record<string, any> = {};
    if (filters.societyId) params.societyId = filters.societyId;
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortDirection) params.sortDirection = filters.sortDirection;
    if (filters.search) params.search = filters.search;
    if (filters.roleType) params.roleType = filters.roleType;
    if (filters.status) params.status = filters.status;
    if (filters.includeChildren) params.includeChildren = filters.includeChildren;
    const { data } = await axiosInstance.get('/external-users-v2', { params });
    return data;
  },

  getOne: async (id: string) => {
    const { data } = await axiosInstance.get<ExternalUser>(`/external-users-v2/${id}`);
    return data;
  },

  /**
   * Exporter les utilisateurs (nom/prénom/mail/société) en CSV.
   * Respecte les filtres affichés. Réservé aux internes (droit `external_user.export`).
   */
  exportAll: async (filters: Record<string, any> = {}): Promise<Blob> => {
    const params: Record<string, any> = {};
    if (filters.societyId) params.societyId = filters.societyId;
    if (filters.search) params.search = filters.search;
    if (filters.roleType) params.roleType = filters.roleType;
    if (filters.status) params.status = filters.status;
    if (filters.includeChildren) params.includeChildren = filters.includeChildren;
    const { data } = await axiosInstance.get('/external-users-v2/export', {
      params,
      responseType: 'blob',
    });
    return data as Blob;
  },

  // ==================== MUTATIONS (ecriture) ====================

  create: async (userData: CreateExternalUserDto) => {
    const { data } = await axiosInstance.post<ExternalUser>('/external-users-v2', userData);
    return data;
  },

  update: async (id: string, userData: UpdateExternalUserDto) => {
    const { data } = await axiosInstance.put<ExternalUser>(`/external-users-v2/${id}`, userData);
    return data;
  },

  delete: async (id: string, reason?: string) => {
    const { data } = await axiosInstance.delete(`/external-users-v2/${id}`, {
      data: { reason },
    });
    return data;
  },

  toggleStatus: async (id: string) => {
    const { data } = await axiosInstance.patch(`/external-users-v2/${id}/toggle-status`);
    return data;
  },

  updatePermissions: async (id: string, permissions: string[]) => {
    const { data } = await axiosInstance.put(`/external-users-v2/${id}/permissions`, {
      permissions,
    });
    return data;
  },

  setPassword: async (id: string, password: string) => {
    const { data } = await axiosInstance.post(`/external-users-v2/${id}/set-password`, { password });
    return data;
  },

  resendWelcomeEmail: async (id: string) => {
    const { data } = await axiosInstance.post(`/external-users-v2/${id}/resend-welcome-email`);
    return data;
  },

  checkEmail: async (email: string, excludeId?: string) => {
    const params: Record<string, string> = { email };
    if (excludeId) params.excludeId = excludeId;
    const { data } = await axiosInstance.get('/external-users-v2/check-email', { params });
    return data;
  },

  // ==================== AUDIT LOGS ====================

  getAuditLogs: async (userId: string, limit = 50): Promise<AuditLog[]> => {
    const { data } = await axiosInstance.get<AuditLog[]>(`/external-users-v2/${userId}/audit-logs`, {
      params: { limit },
    });
    return data;
  },

  // ==================== SOCIETES ====================

  getSocieties: async (userId: string) => {
    const { data } = await axiosInstance.get(`/external-users-v2/${userId}/societies`);
    return data;
  },

  assignSociety: async (userId: string, societyId: string, isPrimary = false) => {
    await axiosInstance.post(`/external-users-v2/${userId}/societies`, {
      societyId,
      isPrimary,
    });
  },

  unassignSociety: async (userId: string, societyId: string) => {
    await axiosInstance.delete(`/external-users-v2/${userId}/societies/${societyId}`);
  },

  setPrimarySociety: async (userId: string, societyId: string) => {
    await axiosInstance.put(`/external-users-v2/${userId}/societies/${societyId}/primary`);
  },
};
